import { Link } from "@/components/ui/link";

import { PageHeader, StatTile } from "@/components/layout-bits";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  StatusBadge,
} from "@/components/ui/primitives";
import { TenantMapSection } from "@/components/tenant-map/tenant-map-section";
import { ErrorState } from "@/components/ui/states";
import {
  useGovernanceSummary,
  useMe,
  useMyAgents,
  useMyDomains,
  useMyTenant,
  useTenants,
} from "@/lib/query/hooks";
import type { TenantStatus } from "@/types/api";

export default function DashboardPage() {
  const { data: me } = useMe();
  if (!me) return null;
  return me.role === "superadmin" ? <SuperadminDashboard /> : <TenantDashboard />;
}

function SuperadminDashboard() {
  const tenants = useTenants();

  const counts = (tenants.data ?? []).reduce(
    (acc, t) => {
      acc.total += 1;
      acc[t.status] += 1;
      return acc;
    },
    { total: 0, active: 0, provisioning: 0, suspended: 0, failed: 0 } as Record<string, number> & {
      total: number;
    } & Record<TenantStatus, number>,
  );

  return (
    <div>
      <PageHeader
        title="Overview"
        description="Platform-wide status across all tenants."
        actions={
          <Button asChild>
            <Link href="/tenants/new">Onboard tenant</Link>
          </Button>
        }
      />

      {tenants.isError ? (
        <ErrorState error={tenants.error} onRetry={() => tenants.refetch()} />
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {tenants.isLoading ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)
          ) : (
            <>
              <StatTile label="Tenants" value={counts.total} accent="primary" />
              <StatTile label="Active" value={counts.active} accent="success" />
              <StatTile label="Provisioning" value={counts.provisioning} accent="warning" />
              <StatTile
                label="Suspended / Failed"
                value={counts.suspended + counts.failed}
                accent="muted"
              />
            </>
          )}
        </div>
      )}

      <div className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent tenants</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {tenants.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : (tenants.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No tenants yet.</p>
            ) : (
              (tenants.data ?? []).slice(0, 5).map((t) => (
                <div key={t.tenant_id} className="flex items-center justify-between">
                  <Link href={`/tenants/${t.slug}`} className="text-sm font-medium hover:underline">
                    {t.display_name}
                  </Link>
                  <StatusBadge status={t.status} />
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function TenantDashboard() {
  const tenant = useMyTenant();
  const domains = useMyDomains();
  const agents = useMyAgents();

  return (
    <div>
      <PageHeader
        title={tenant.data?.slug ? `Tenant: ${tenant.data.slug}` : "Your workspace"}
        description="Your tenant's isolated data."
      />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatTile
          label="Status"
          value={
            tenant.data ? (
              <StatusBadge status={tenant.data.status} />
            ) : (
              <Skeleton className="h-7 w-16" />
            )
          }
          accent="primary"
        />
        <StatTile
          label="Domains"
          value={domains.isLoading ? "…" : (domains.data?.length ?? 0)}
          accent="success"
        />
        <StatTile
          label="Agents"
          value={agents.isLoading ? "…" : (agents.data?.length ?? 0)}
          accent="success"
        />
      </div>
      <GovernanceTile />

      <div className="mt-8">
        <TenantMapSection />
      </div>

      <p className="mt-6 text-sm text-muted-foreground">
        Use the sidebar to browse your domains and agents — data is fully isolated to this tenant.
      </p>
    </div>
  );
}

/**
 * Repo Governance at a glance (ADR-016).
 *
 * Renders nothing at all when the tenant has not connected GitHub, or when the
 * deployment has governance switched off — a dashboard tile advertising a
 * feature that isn't available is worse than no tile.
 */
function GovernanceTile() {
  const summary = useGovernanceSummary();
  if (summary.isLoading || summary.isError || !summary.data?.connected) return null;

  const { open_critical, open_high, repos_enabled, reviews_last_7d } = summary.data;
  const urgent = open_critical + open_high;

  return (
    <Card className="mt-6">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Repo Governance</CardTitle>
        <Button size="sm" variant="ghost" asChild>
          <Link href="/governance/security">Review findings</Link>
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          <StatTile
            label="Open critical / high"
            value={urgent}
            accent={urgent > 0 ? "destructive" : "success"}
          />
          <StatTile label="Repositories governed" value={repos_enabled} accent="primary" />
          <StatTile label="Reviews (7 days)" value={reviews_last_7d} accent="muted" />
        </div>
        {urgent > 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">
            {urgent} unreviewed {urgent === 1 ? "change" : "changes"} to what your AI coding agents
            are allowed to do.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
